import React, { useEffect, useState } from "react";
import "./styles/PhishingManager.module.scss";
import CampaignSetup from "./CampaignSetup";
import { Pagination, Select, Spin } from "antd";
import { Icon } from "Components";
import { GetGroupsListRequest, GetGroupsListResponse } from "Services/Api/Groups/Types";
import { DEFAULT_TABLE_REQUEST_VALUES } from "Pages/Groups/Group/Group";
import { useLazyQuery } from "@apollo/client";
import { GET_GROUPS_LIST } from "Services/Api/Groups/Queries";
import { authStore } from "Stores/AuthStore";
import { SystemRoleType } from "Services/Api/Roles/Types";
import { getPhishingCampaignData, getPhishingCampaignsForTenant } from "Services/Api/PhishingManager/PhishingManager.Service";
import WhiteListing from "./Phishing/Whitelisting";
import CampaignLimitWarningModal from "./Phishing/CampaignLimitWarningModal";
import DiscardCampaignModal from "./Phishing/DiscardCampaignModal";
import CampaignList from "./Phishing/CampaignList";
import EmptyState from "./Phishing/EmptyState";
import NoTenantSelectedEmptyState from "./Phishing/NoTenantSelectedEmptyState";
import CampaignLimitToast from "./Phishing/CampaignLimitToast";
import { DEFAULT_CAMPAIGNS_PAGESIZE } from "./PhishingManager.constants";

export interface CampaignData {
  id: number | string;
  name: string;
  status: string;
  campaign_type: string;
  schedule_datetime: string | null;
  schedule_timezone: string | null;
  campaign_duration: number;
  image_url: string;
}

export interface CampaignThresholdData {
  campaign_count: number;
  first_campaign_created_at: string;
  tenant_campaigns_threshold: number;
  current_period_started_at: string;
  threshold_period_months: number;
  is_threshold_reached: boolean;
}

const PhishingManager: React.FC = () => {

  const [campaignThresholdData, setCampaignThresholdData] = useState<CampaignThresholdData | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedTenantId, setSelectedTenantId] = useState<string | number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [phishingArray, setPhishingArray] = useState<CampaignData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [itemToDiscard, setItemToDiscard] = useState<string | null>(null);
  const [warningModal, setWarningModal] = useState(false);
  const [newCampaignSetup, setNewCampaignSetup] = useState(false);
  const statusStyles: Record<string, { background: string; color: string }> = {
    Scheduled: { background: "#EAEAEB", color: "#000" },
    Live: { background: "#F0EFFD", color: "#6B5AED" },
    Testing: { background: "#FEF6E9", color: "#000" },
    Draft: { background: "#FEF6E9", color: "#000" },
    Complete: { background: "#CDE7CF", color: "#2E7D32" },
    Cancelled: { background: "#FBEAEA", color: "#D32F2F" },
    Failed: { background: "#FBEAEA", color: "#D32F2F" },
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_CAMPAIGNS_PAGESIZE);
  const threshold = campaignThresholdData?.tenant_campaigns_threshold ?? 0;

  const handleMenuClick = (itemId: string) => (info: any) => {
    const menuKey = info.key.toString();
    if (menuKey === 'discard') {
      setItemToDiscard(itemId);
      setIsModalVisible(true);
    } else {
      console.log(`${menuKey} action for:`, itemId);
    }
  };

  const [getGroup, result] = useLazyQuery<
    GetGroupsListResponse,
    GetGroupsListRequest
  >(GET_GROUPS_LIST, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const TENANTS: { label: string; value: string }[] =
    result?.data?.listGroups?.map(el => ({
      label: el.name,
      value: String(el.id),
    })) ?? [];

  const fetchTenants = (
    values: GetGroupsListRequest = DEFAULT_TABLE_REQUEST_VALUES
  ) => {
    setIsLoading(true);
    const variables: GetGroupsListRequest = {
      sort: values.sort,
      filters: {
        ...values.filters,
        depth: 1,
        parentId: null
      }
    };

    setIsLoading(false);
    if (result.refetch) {
      result.refetch(variables);
    } else {
      getGroup({
        variables
      });
    }
  };

  const confirmDiscard = () => {
    if (itemToDiscard) {
      setPhishingArray(prev => prev.filter(el => el.id !== Number(itemToDiscard)));
    }
    setIsModalVisible(false);
    setItemToDiscard(null);
  };

  const confirmWarningDiscard = () => {
    newCampaign();
    setWarningModal(false);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setItemToDiscard(null);
  };

  const closeWarningModal = () => {
    setWarningModal(false);
  };

  const handleCreateCampaign = () => {
    if (phishingArray.length === (threshold - 1)) {
      setWarningModal(true);
    } else if (!!campaignThresholdData?.is_threshold_reached && phishingArray.length >= threshold) {
      setErrorMessage("Campaign limit reached. No more campaigns can be created.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    } else {
      if (!!campaignThresholdData) {
        newCampaign();
      } else {
        if (!!errorMessage) {
          setErrorMessage(errorMessage);
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
          }, 3000)
        }
      }
    }
  };

  const newCampaign = () => {
    setNewCampaignSetup(true);
  }

  const PrevIcon = () => (
    <Icon type="previousPagination" />
  );

  const NextIcon = () => (
    <Icon type="nextPagination" />
  );

  const paginationItemRender = (
    page: number,
    type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
    originalElement: React.ReactNode
  ) => {
    if (type === "prev") {
      return (
        <span className="ml-pagination-btn">
          <PrevIcon />
        </span>
      );
    }

    if (type === "next") {
      return (
        <span className="ml-pagination-btn">
          <NextIcon />
        </span>
      );
    }

    return originalElement;
  };

  const isSuperAdmin =
    authStore.currentUser?.primarySystemRole?.role ===
    SystemRoleType.SuperAdmin;


  const handleTenantChange = async (id: string | number) => {
    setIsLoading(true);
    setCurrentPage(1);
    await fetchCampaigns(id);
    await fetchCampaignsData(id);
    setSelectedTenantId(id);
  }

  const fetchCampaigns = async (value: any, pagination?: any) => {
    try {
      setIsLoading(true);
      const page = pagination ?? 1
      const response = await getPhishingCampaignsForTenant(value, page);
      if (response?.meta) {
        setTotalItems(response.meta.total);
        setPageSize(response.meta.per_page);
        setCurrentPage(response.meta.current_page);
      }
      const campaignData = response?.data.map((el: CampaignData) => {
        let formattedStatus = "";
        switch (el.status) {
          case "draft": formattedStatus = "Draft"; break;
          case "in_testing": formattedStatus = "Testing"; break;
          case "completed": formattedStatus = "Complete"; break;
          case "scheduled": formattedStatus = "Scheduled"; break;
          case "in_progress": formattedStatus = "Live"; break;
          case "cancelled": formattedStatus = "Cancelled"; break;
          case "failed": formattedStatus = "Failed"; break;
          default: formattedStatus = el.status;
        }
        return { ...el, status: formattedStatus };
      }) || [];
      setPhishingArray(campaignData);
    } catch (error) {
      setPhishingArray([]);
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampaignsData = async (value: any) => {
    setIsLoading(true);
    try {
      const response = await getPhishingCampaignData(value);
      setCampaignThresholdData(response.data);
      if (response.data?.is_threshold_reached) {
        setErrorMessage("Campaign limit reached. No more campaigns can be created.");
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
      } else {
        setErrorMessage("");
      }
    } catch (error: any) {
      setCampaignThresholdData(null);
      const errorMessage =
        error?.data?.errors[0] ||
        error?.data?.message ||
        "";
      if (!!errorMessage) {
        setErrorMessage(errorMessage);
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000)
      }
      console.error("Failed to fetch campaigns:", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && TENANTS.length > 0 && !selectedTenantId) {
      setSelectedTenantId(TENANTS[0].value);
    }
  }, [TENANTS]);

  return (
    <Spin spinning={isLoading}>
      <div className="phishingManager">
        <CampaignLimitToast
          visible={showToast}
          message={errorMessage ?? ""}
          onClose={() => setShowToast(false)}
        />
        {newCampaignSetup ? <CampaignSetup thresholdData={campaignThresholdData} tenantId={selectedTenantId} onBack={() => setNewCampaignSetup(false)} /> :
          <>  <div className="pm-header-row">
            <div className="pm-header-content">
              <div className="pm-title-group">
                <h3 className="pm-title">Phishing Manager</h3>
                <p className="pm-subtitle">Create, monitor, and learn from phishing simulations</p>
              </div>
              <div className="pm-actions">
                <label htmlFor="pm-client-select" className="pm-client-label">Tenant</label>
                <Select
                  id="pm-client-select"
                  dropdownClassName="cs-select-dropdown"
                  placeholder="Select a Tenant"
                  className="mw-150"
                  value={selectedTenantId}
                  loading={isLoading}
                  onChange={(value) => handleTenantChange(value)}
                  dropdownStyle={{ fontFamily: "Inter", fontSize: "16px", color: "#000" }}
                  showSearch
                  suffixIcon={<Icon type="campaignDropdown" />}
                  style={{ width: "100%", fontFamily: 'Inter' }}
                  options={TENANTS}
                />

                <button
                  type="button"
                  className={`pm-new-campaign-btn ${(totalItems >= threshold) ? (!selectedTenantId
                    ? "pm-new-campaign-btn-toggle"
                    : !!errorMessage
                      ? "pm-new-campaign-btn-disable-toggle"
                      : ""
                  ) : ""}`}

                  onClick={handleCreateCampaign}
                  disabled={
                    !selectedTenantId
                  }
                  aria-label="Create new campaign"
                >
                  <Icon type="campaignAdd" />
                  <span className="pm-new-campaign-text">New Campaign</span>
                </button>
              </div>
            </div>
          </div>

            {
              phishingArray.length === 0 ? !selectedTenantId ?
                <NoTenantSelectedEmptyState />
                : (
                  <EmptyState
                    title="No campaigns yet"
                    description=" You haven’t created any phishing campaigns yet. Start by creating a campaign
                        to simulate phishing attacks and measure how your users respond.
   "
                    onAction={handleCreateCampaign}
                  />
                ) : (
                <>
                  <div className="pm-campaigns-header">Campaigns</div>
                  <CampaignList
                    campaigns={phishingArray}
                    statusStyles={statusStyles}
                    onMenuClick={handleMenuClick}
                  />
                  <div className="pm-pagination-wrapper">
                    <Pagination
                      itemRender={paginationItemRender}
                      current={currentPage}
                      total={totalItems}
                      pageSize={pageSize}
                      onChange={(page) => {
                        setCurrentPage(page);
                        fetchCampaigns(selectedTenantId, page);
                      }}
                      showSizeChanger={false}
                      hideOnSinglePage={true}
                      responsive={true}
                    />
                  </div>
                </>
              )
            }
            <CampaignLimitWarningModal
              visible={warningModal}
              onClose={closeWarningModal}
              onConfirm={confirmWarningDiscard}
            />
            <DiscardCampaignModal
              visible={isModalVisible}
              onClose={closeModal}
              onConfirm={confirmDiscard}
            />
            <WhiteListing />
          </>
        }

      </div >
    </Spin>
  );
};

export default PhishingManager;